import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearQuizAttemptDirty,
  confirmUnsavedQuizNavigation,
  setQuizAttemptConfirmationHandler,
  setQuizAttemptDirty,
} from "../utils/unsavedQuizGuard";

const GUARDED_CLICK_FLAG = Symbol("unsaved-quiz-link-checked");
const allowedLinkReplays = new WeakSet();

export default function useUnsavedQuizAttemptGuard(isDirty) {
  const attemptIdRef = useRef(Symbol("quiz-attempt"));
  const pendingDecisionRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);

  const requestNavigationDecision = useCallback(() => {
    if (pendingDecisionRef.current?.promise) {
      return pendingDecisionRef.current.promise;
    }

    let resolveDecision;
    const promise = new Promise((resolve) => {
      resolveDecision = resolve;
    });

    pendingDecisionRef.current = { promise, resolve: resolveDecision };
    setModalOpen(true);
    return promise;
  }, []);

  const resolveNavigationDecision = useCallback((shouldLeave) => {
    const pending = pendingDecisionRef.current;
    pendingDecisionRef.current = null;
    setModalOpen(false);
    pending?.resolve(Boolean(shouldLeave));
  }, []);

  useEffect(() => {
    const attemptId = attemptIdRef.current;
    setQuizAttemptDirty(attemptId, isDirty);
    setQuizAttemptConfirmationHandler(
      attemptId,
      isDirty ? requestNavigationDecision : null
    );

    if (!isDirty && pendingDecisionRef.current) {
      resolveNavigationDecision(true);
    }

    return () => {
      clearQuizAttemptDirty(attemptId);
    };
  }, [isDirty, requestNavigationDecision, resolveNavigationDecision]);

  useEffect(
    () => () => {
      pendingDecisionRef.current?.resolve(false);
      pendingDecisionRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handleDocumentLinkClick = (event) => {
      if (event[GUARDED_CLICK_FLAG]) return;

      const link = event.target.closest?.("a[href]");
      if (!link || event.defaultPrevented) return;
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        link.target === "_blank" ||
        link.hasAttribute("download")
      ) {
        return;
      }
      if (allowedLinkReplays.has(link)) {
        allowedLinkReplays.delete(link);
        return;
      }

      event[GUARDED_CLICK_FLAG] = true;
      event.preventDefault();
      event.stopPropagation();

      confirmUnsavedQuizNavigation().then((shouldLeave) => {
        if (!shouldLeave || !link.isConnected) return;
        allowedLinkReplays.add(link);
        link.click();
      });
    };

    document.addEventListener("click", handleDocumentLinkClick, true);
    return () =>
      document.removeEventListener("click", handleDocumentLinkClick, true);
  }, [isDirty]);

  return {
    modalOpen,
    stayInQuiz: () => resolveNavigationDecision(false),
    leaveQuiz: () => resolveNavigationDecision(true),
  };
}
